import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  BarChart3, TrendingUp, Users, DollarSign, Trophy, Medal 
} from 'lucide-react';

const AnalyticsPage = () => {
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await analyticsAPI.getPartnerPerformance('monthly');
      setPerformance(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getTierBadge = (tier) => {
    const styles = {
      'bronze': 'tier-bronze',
      'silver': 'tier-silver',
      'gold': 'tier-gold',
      'platinum': 'tier-platinum',
    };
    return styles[tier] || 'tier-bronze';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-amber-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-slate-400" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-orange-400" />;
    return <span className="text-lg font-bold text-muted-foreground">#{rank}</span>;
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Calculate totals
  const totals = performance.reduce((acc, p) => ({
    leads: acc.leads + p.total_leads,
    bookings: acc.bookings + p.bookings,
    revenue: acc.revenue + p.total_revenue,
    commission: acc.commission + p.total_commission,
  }), { leads: 0, bookings: 0, revenue: 0, commission: 0 });

  const avgConversion = performance.length > 0
    ? (performance.reduce((sum, p) => sum + p.conversion_rate, 0) / performance.length).toFixed(1)
    : 0;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Partner Performance</h1>
        <p className="text-muted-foreground">Track and analyze partner metrics</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Users className="h-4 w-4" />
              <span className="text-sm">Total Leads</span>
            </div>
            <p className="text-2xl font-bold">{totals.leads}</p>
          </CardContent>
        </Card>
        
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm">Bookings</span>
            </div>
            <p className="text-2xl font-bold">{totals.bookings}</p>
          </CardContent>
        </Card>
        
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <BarChart3 className="h-4 w-4" />
              <span className="text-sm">Avg Conversion</span>
            </div>
            <p className="text-2xl font-bold">{avgConversion}%</p>
          </CardContent>
        </Card>
        
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <DollarSign className="h-4 w-4" />
              <span className="text-sm">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(totals.revenue)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            Partner Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          {performance.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No performance data yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {performance.map((partner, index) => (
                <div 
                  key={partner.partner_id} 
                  className={`flex items-center gap-4 p-4 rounded-lg ${
                    index === 0 ? 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800' : 'bg-muted/50'
                  }`}
                >
                  <div className="w-10 flex justify-center">
                    {getRankIcon(partner.ranking)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{partner.partner_name}</h3>
                      <Badge className={getTierBadge(partner.tier)}>
                        {partner.tier}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span>{partner.total_leads} leads</span>
                      <span>{partner.site_visits} visits</span>
                      <span>{partner.bookings} bookings</span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="font-semibold text-emerald-600">
                      {partner.conversion_rate}%
                    </p>
                    <p className="text-sm text-muted-foreground">conversion</p>
                  </div>
                  
                  <div className="text-right hidden md:block">
                    <p className="font-semibold">{formatCurrency(partner.total_revenue)}</p>
                    <p className="text-sm text-muted-foreground">revenue</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsPage;
